import { DateRange } from '@angular/material/datepicker';
import { FilterCodec, FormatedSavedFilter, SavedFilterService } from '@teammood/filter';
import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import { ReportingService } from './reporting.service';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { LoggerService } from '@teammood/shared/util/logger';
import { ReportWithChartData } from './reporting.type';
import { format } from 'date-fns-tz';
import { SavedFilterChange } from "../filter/saved-filter/saved-filter.component";
import { ActivatedRoute } from "@angular/router";
import { Subject, takeUntil, tap } from "rxjs";
import { addDays, parse } from "date-fns";

const HELP = {
    people: {
        title: 'reporting_help_people',
        link: 'https://help.teammood.com/en/articles/5982013-the-reporting#h_725687be7e'
    },
    global: {
        title: 'reporting_help_global_participation',
        link: 'https://help.teammood.com/en/articles/5982013-the-reporting#h_eb3d1a40cb'
    },
    participation: {
        title: 'reporting_help_distribution_participation',
        link: 'https://help.teammood.com/en/articles/5982013-the-reporting#h_eb3d1a40cb'
    },
    commented: {
        title: 'reporting_help_commented',
        link: 'https://help.teammood.com/en/articles/5982013-the-reporting#h_e46fb90257'
    },
    score: {
        title: 'reporting_help_score',
        link: 'https://help.teammood.com/en/articles/5982013-the-reporting#h_9b191d8a49'
    },
    distribution: {
        title: 'reporting_help_distribution',
        link: 'https://help.teammood.com/en/articles/5982013-the-reporting#h_7506fc7fb6'
    },
    noteworthy: {
        title: 'reporting_help_noteworthy_days',
        link: 'https://help.teammood.com/en/articles/5982013-the-reporting#h_896539f144'
    }
}

@Component({
    selector: 'reporting',
    templateUrl: './reporting.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportingComponent implements OnInit, OnDestroy {
    
    pageTitle = 'Reporting';
    showSpinner = false;
    selectedFilter: FormatedSavedFilter;
    dateInterval: DateRange<Date>;
    report: ReportWithChartData; 
    help = HELP

    private _destroy$ = new Subject<void>();

    constructor(
        private logger: LoggerService,
        private reportingService: ReportingService,
        private savedFilterService: SavedFilterService,
        private snackBarService: SnackBarService,
        private filterCodec: FilterCodec,
        private route: ActivatedRoute,
    ) { }


    async ngOnInit() {

        this.route.queryParams.pipe(
            tap(async params => {
                const start = params['start'];
                const end = params['end'];

                const filter = params['filter'];

                this.showSpinner = true;

                if (start && end) {
                    const dateRangeFromUrl: DateRange<Date> = new DateRange(parse(start, 'dd-MM-yyyy', new Date()), parse(end, 'dd-MM-yyyy', new Date()));
                    this.savedFilterService.saveDateRange(dateRangeFromUrl);
                } else {
                    this.savedFilterService.saveDateRange(new DateRange<Date>(addDays(new Date(), -30), new Date()));
                }

                this.dateInterval = this.savedFilterService.getDateRange()

                if (filter) {
                    this.savedFilterService.saveFilter(this.filterCodec.decode(filter));
                }

                await this.loadReport();

            }),
            takeUntil(this._destroy$)
        ).subscribe();
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    async onFilterChange(change: SavedFilterChange) {
        this.selectedFilter = change.filter ? change.filter : this.selectedFilter;
        this.dateInterval = change.dateInterval ? change.dateInterval : this.dateInterval;
        await this.loadReport();
    }


    async loadReport() {
        if (!this.selectedFilter || !this.dateInterval) {
            return;
        }

        const filterPredicates = this.selectedFilter.decodedFilter.predicates;
        const dateInterval = {
            start: format(this.dateInterval.start, 'dd-MM-yyyy'),
            end: format(this.dateInterval.end, 'dd-MM-yyyy'),
        };
        this.showSpinner = true;

        try {
            this.report = await this.reportingService.reporting(filterPredicates, dateInterval);
        } catch (error) {
            this.logger.error('error loading the report', error);
            this.snackBarService.error(error.message);
            this.report = null;
        } finally {
            this.showSpinner = false;
        }
    }
}
